import { createSelector } from 'reselect';

import { activeUploadStates, pausableStates } from './constants';
import { initialUploadProgress, type Upload, type UploaderState, type UploaderStore, UploadStatus } from './types';
import { isFinishedUploading, isLarge, uploadHasUploadInfo } from './utils';

const uploaderSelector = ({ uploader }: UploaderStore): UploaderState => uploader;
const uploadIdSelector = (_state: UploaderStore, uploadId: Upload['id']) => uploadId;

export const uploadArraySelector = createSelector(uploaderSelector, ({ uploadsArray }) => uploadsArray);

const currentUploadingInfoSelector = createSelector(
  uploaderSelector,
  ({ currentUploadingInfo }) => currentUploadingInfo,
);

export const queuedOrPreparingUploadsSelector = createSelector(uploadArraySelector, (uploads): Upload[] =>
  uploads.filter((u) => u.status === UploadStatus.queued || u.status === UploadStatus.preparing),
);

export const uploadingSpeedSelector = createSelector(currentUploadingInfoSelector, ({ speed }) => speed);

export const pauseableUploadsSelector = createSelector(uploadArraySelector, (uploads): Upload[] =>
  uploads.filter((u) => pausableStates.includes(u.status)),
);

export const uploadsCurrentlyUploadingSelector = createSelector(uploadArraySelector, (uploads): Upload[] =>
  uploads.filter((u) => u.status === UploadStatus.uploading || u.status === UploadStatus.preparing),
);

/**
 * Sum of bytes actively on the wire for every upload with status = uploading.
 *
 * - Small uploads: the whole file is transferred in one XHR → use file.size.
 * - Large (multipart) uploads: each chunk is marked active when the scheduler
 *   starts that chunk task, before the XHR exists. This avoids under-counting
 *   in-flight bytes during the gap between task start and XHR registration.
 */
export const totalInFlightBytesSelector = createSelector(uploadArraySelector, (uploads): number =>
  uploads
    .filter((u) => u.status === UploadStatus.uploading)
    .reduce((sum, u) => {
      if (isLarge(u)) {
        return sum + Object.values(u.activeChunks ?? {}).reduce((chunkSum, { size }) => chunkSum + size, 0);
      }

      return sum + u.file.size;
    }, 0),
);

export const hasCurrentlyUploadingUploadsSelector = createSelector(
  uploadsCurrentlyUploadingSelector,
  (uploads) => !!uploads.length,
);

export const activeUploadsSelector = createSelector(uploadArraySelector, (uploads): Upload[] =>
  uploads.filter((u) => activeUploadStates.includes(u.status)),
);

export const activeUploadCountSelector = createSelector(
  uploadArraySelector,
  (uploads) => uploads.filter((u) => activeUploadStates.includes(u.status)).length,
);

export const activeUploadIdsSelector = createSelector(uploadArraySelector, (uploads): string[] =>
  uploads.filter((u) => activeUploadStates.includes(u.status)).map((u) => u.id),
);

export const completedUploadsSelector = createSelector(uploadArraySelector, (uploads): Upload[] =>
  uploads.filter((u) => u.status === UploadStatus.completed),
);

export const completedUploadIdsSelector = createSelector(completedUploadsSelector, (uploads): string[] =>
  uploads.map(({ id }) => id),
);

export const completedUploadCountSelector = createSelector(
  uploadArraySelector,
  (uploads) => uploads.filter((u) => u.status === UploadStatus.completed).length,
);

export const pausedUploadsSelector = createSelector(uploadArraySelector, (uploads): Upload[] =>
  uploads.filter((u) => u.status === UploadStatus.paused),
);

export const pausedUploadCountSelector = createSelector(
  uploadArraySelector,
  (uploads) => uploads.filter((u) => u.status === UploadStatus.paused).length,
);

export const hasPausedUploadsSelector = createSelector(
  pausedUploadsSelector,
  (pausedUploads) => pausedUploads.length > 0,
);

export const queuedUploadsSelector = createSelector(uploadArraySelector, (uploads): Upload[] =>
  uploads.filter((u) => u.status === UploadStatus.queued),
);

export const notStartedUploadsIdsSelector = createSelector(uploadArraySelector, (uploads) =>
  uploads.reduce<string[]>((acc, curr) => {
    if (!curr.clipId) {
      acc.push(curr.id);
    }

    return acc;
  }, []),
);

export const activeNotCompletedUploadsSelector = createSelector(uploadArraySelector, (uploads) =>
  uploads.reduce<Upload[]>((acc, curr) => {
    if (
      curr.status === UploadStatus.uploading ||
      curr.status === UploadStatus.queued ||
      curr.status === UploadStatus.preparing ||
      curr.status === UploadStatus.paused
    ) {
      acc.push(curr);
    }

    return acc;
  }, []),
);

export const failedUploadsSelector = createSelector(uploadArraySelector, (uploads): Upload[] =>
  uploads.filter((u) => u.status === UploadStatus.failed),
);

export const failedUploadIdsSelector = createSelector(failedUploadsSelector, (uploads): string[] =>
  uploads.map(({ id }) => id),
);

export const failedUploadCountSelector = createSelector(
  uploadArraySelector,
  (uploads) => uploads.filter((u) => u.status === UploadStatus.failed).length,
);

export const hasUploadsSelector = createSelector(
  uploadArraySelector,
  (uploads) => uploads.filter(({ status }) => status !== UploadStatus.aborted).length > 0,
);

export const uploaderIsUploadingSelector = createSelector(
  uploadArraySelector,
  (uploads): boolean =>
    !!uploads.filter(
      (u) =>
        u.status === UploadStatus.uploading || u.status === UploadStatus.queued || u.status === UploadStatus.preparing,
    ).length,
);

export const nextUploadIdSelector = createSelector(queuedUploadsSelector, (uploads) => uploads[0]?.id);

export const queuedUploadSizesSelector = createSelector(queuedUploadsSelector, (uploads) =>
  uploads.map((upload) => upload.file.size),
);

export const isFinishedUploadingSelector = createSelector(uploadArraySelector, (uploads) =>
  isFinishedUploading(uploads),
);

export const allUploadProgressSelector = createSelector([uploaderSelector], ({ uploadProgress }) => uploadProgress);

export const totalUploadedSizeSelector = createSelector(
  currentUploadingInfoSelector,
  ({ uploadedBytes }) => uploadedBytes,
);

export const totalUploadSizeLeftSelector = createSelector(
  pauseableUploadsSelector,
  allUploadProgressSelector,
  (uploads, uploadsProgress) =>
    uploads.reduce((acc, curr) => {
      if (curr.clipId && uploadsProgress[curr.id]) {
        const uploadLeft = curr.file.size - uploadsProgress[curr.id].progress;

        return acc + uploadLeft;
      }

      return acc + curr.file.size;
    }, 0),
);

export const uploadProgressSelector = createSelector(
  [allUploadProgressSelector, uploadIdSelector],
  (uploadProgress, uploadId) => uploadProgress[uploadId] || initialUploadProgress,
);

export const uploadByIdSelector = createSelector(uploadArraySelector, uploadIdSelector, (uploads, uploadId) =>
  uploads.find(({ id }) => id === uploadId),
);

export const largeUploadByIdSelector = createSelector(uploadArraySelector, uploadIdSelector, (uploads, uploadId) => {
  const upload = uploads.find(({ id }) => id === uploadId);

  if (upload && isLarge(upload)) {
    return upload;
  }
});

export const largeUploadWithS3InfoByIdSelector = createSelector(largeUploadByIdSelector, (upload) => {
  if (upload && uploadHasUploadInfo(upload)) {
    return upload;
  }
});

export const smallUploadByIdSelector = createSelector(uploadArraySelector, uploadIdSelector, (uploads, uploadId) => {
  const upload = uploads.find(({ id }) => id === uploadId);

  if (upload && !isLarge(upload)) {
    return upload;
  }
});

export const uploadedPartsSelector = createSelector(uploadByIdSelector, (upload) => upload?.s3Info.uploadedParts ?? []);

export const smallUploadUrlInfoSelector = createSelector(
  smallUploadByIdSelector,
  (upload) => upload?.s3Info.uploadUrlInfo,
);
export const largeUploadUrlInfoSelector = createSelector(
  largeUploadByIdSelector,
  (upload) => upload?.s3Info.uploadUrlInfo,
);

export const uploadInstabilityDetectedSelector = ({ uploader }: UploaderStore) => uploader.uploadInstabilityDetected;
